import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import Table from 'components/Common/Table';
import MessageBatchesTab from "./MessageBatchesTab";
import {
  ApiOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  LoadingOutlined,
  SyncOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SendOutlined,
  StarOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import EmailEditor from "react-email-editor";

import QEditor from "components/Editors/QEditor";
import CodeMirrorEditor from "components/Editors/CodeMirrorEditor";
import useAuthedMedia from "components/Communication/useAuthedMedia";
import WhatsAppTextEditor from "components/WhatsApp/WhatsAppTextEditor";
import { WhatsAppPreview } from "components/WhatsApp/WhatsAppLayout";
import { useNotification } from "NotificationContext";
import * as api from "api/activityTemplates";
import ChatbotConfigurationTab from "./ChatbotConfigurationTab";
import MessageLogsDrawer from "./MessageLogsDrawer";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Masters behind the templates a rep sends from a lead activity:
 * categories, email + WhatsApp templates, and the per-product email/WhatsApp
 * sending configurations.
 */

// Meta writes placeholders as {{1}}, {{2}} ... in a component's text.
const PLACEHOLDER_RE = /{{\s*(\d+)\s*}}/g;

const placeholderIndexes = (text) => {
  const found = [];
  let match = PLACEHOLDER_RE.exec(text || "");
  while (match) {
    if (!found.includes(match[1])) found.push(match[1]);
    match = PLACEHOLDER_RE.exec(text || "");
  }
  PLACEHOLDER_RE.lastIndex = 0;
  return found.sort((a, b) => Number(a) - Number(b));
};

const componentText = (components, type) => {
  const component = (components || []).find(
    (item) => String(item?.type || "").toUpperCase() === type
  );
  if (!component) return "";
  if (type === "HEADER" && String(component.format || "TEXT").toUpperCase() !== "TEXT") {
    return "";
  }
  return component.text || "";
};

const useProducts = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api
      .loadProducts()
      .then((response) => setProducts(api.rowsOf(response)))
      .catch(() => setProducts([]));
  }, []);
  return products;
};

const productOptions = (products) =>
  products.map((product) => ({ label: product.name, value: product.id }));

const errorText = (err) => {
  const data = err?.response?.data;
  if (!data) return "Something went wrong.";
  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.message) return data.message;
  return Object.entries(data)
    .map(([field, messages]) => `${field}: ${[].concat(messages).join(" ")}`)
    .join(" | ");
};

/** Markup the rich editor can't hold: layout tables, style blocks, a whole document. */
const isRawHtml = (html) => /<(table|style|html|body|div[^>]*style=)/i.test(html || "");

/** A builder document with something in it - an empty `{}` is no design. */
const hasDesign = (design) => !!design?.body?.rows?.length;

/**
 * A builder document holding existing HTML as a single HTML block, so a
 * template written by hand opens with its content instead of a blank canvas
 * (where saving would replace it with nothing).
 */
const htmlDesign = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const styles = Array.from(doc.querySelectorAll("style"))
    .map((style) => style.outerHTML)
    .join("\n");
  doc.querySelectorAll("style").forEach((style) => style.remove());
  return {
    counters: { u_row: 1, u_column: 1, u_content_html: 1 },
    body: {
      id: "body",
      rows: [
        {
          id: "row-1",
          cells: [1],
          columns: [
            {
              id: "column-1",
              contents: [
                {
                  id: "html-1",
                  type: "html",
                  values: {
                    html: `${styles}\n${doc.body.innerHTML}`.trim(),
                    containerPadding: "0px",
                    hideDesktop: false,
                  },
                },
              ],
              values: { padding: "0px" },
            },
          ],
          values: { padding: "0px", backgroundColor: "" },
        },
      ],
      values: { contentWidth: "700px", backgroundColor: "#ffffff" },
    },
  };
};

const activeTag = (value) =>
  value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>;

// ========== Template categories ==========

const CategoryTab = () => {
  const notify = useNotification();
  const products = useProducts();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadCategories()));
    } catch (err) {
      notify("error", "Could not load categories", errorText(err));
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (record) => {
    setEditing(record || null);
    setOpen(true);
    form.setFieldsValue(
      record || { name: "", product: undefined, description: "", is_active: true }
    );
  };

  const save = async (values) => {
    try {
      if (editing) await api.updateCategory(editing.id, values);
      else await api.createCategory(values);
      notify("success", `Category ${editing ? "updated" : "created"}`);
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save category", errorText(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteCategory(id);
      notify("success", "Category deleted");
      load();
    } catch (err) {
      notify("error", "Could not delete category", errorText(err));
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: "Product", dataIndex: "product_name" },
    { title: "Description", dataIndex: "description", ellipsis: true },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          <a onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          <Popconfirm title="Delete this category?" onConfirm={() => remove(record.id)}>
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          A category belongs to one product. Templates are filtered by the product you pick.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Add Category
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <Modal
        title={editing ? "Edit Category" : "Add Category"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Product" name="product" rules={[{ required: true }]}>
            <Select options={productOptions(products)} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ========== Email templates ==========

const EmailTemplateTab = () => {
  const notify = useNotification();
  const products = useProducts();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  const [formProduct, setFormProduct] = useState(null);
  // "rich" is the plain editor; "design" is the drag-and-drop builder, which
  // round-trips through its own document rather than the exported HTML.
  const [mode, setMode] = useState("rich");
  const [design, setDesign] = useState(null);
  const builderRef = useRef(null);
  const [previewing, setPreviewing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadEmailTemplates()));
    } catch (err) {
      notify("error", "Could not load templates", errorText(err));
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
    api
      .loadVariables()
      .then((response) => setVariables(response.data?.variables || []))
      .catch(() => setVariables([]));
  }, [load]);

  // Category list follows the product chosen in the form.
  useEffect(() => {
    if (!formProduct) {
      setCategories([]);
      return;
    }
    api
      .loadCategories({ product: formProduct, is_active: true })
      .then((response) => setCategories(api.rowsOf(response)))
      .catch(() => setCategories([]));
  }, [formProduct]);

  const openForm = (record) => {
    setEditing(record || null);
    setFormProduct(record?.product || null);
    setHtml(record?.html_content || "");
    setDesign(hasDesign(record?.design_json) ? record.design_json : null);
    // A template built visually reopens in the builder; anything else opens in
    // the editor it was written with.
    // Hand-written HTML (tables, inline styles) opens as source - the rich
    // editor would rewrite it into its own markup and save that over it.
    setMode(
      hasDesign(record?.design_json)
        ? "design"
        : isRawHtml(record?.html_content)
          ? "html"
          : "rich"
    );
    setOpen(true);
    form.setFieldsValue(
      record || {
        title: "",
        product: undefined,
        template_category: undefined,
        subject: "",
        plain_text_content: "",
        description: "",
        is_active: true,
      }
    );
  };

  const exportFromBuilder = () =>
    new Promise((resolve) => {
      const editor = builderRef.current?.editor;
      if (!editor) {
        resolve(null);
        return;
      }
      editor.exportHtml((data) => resolve(data));
    });

  const save = async (values) => {
    let payload = { ...values, html_content: html, design_json: null };

    if (mode === "design") {
      const exported = await exportFromBuilder();
      if (!exported?.html) {
        notify("error", "Could not read the design", "The builder is still loading.");
        return;
      }
      // Keep both: the HTML is what gets sent, the design is what reopens.
      payload = { ...values, html_content: exported.html, design_json: exported.design };
    }

    try {
      if (editing) await api.updateEmailTemplate(editing.id, payload);
      else await api.createEmailTemplate(payload);
      notify("success", `Template ${editing ? "updated" : "created"}`);
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save template", errorText(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteEmailTemplate(id);
      notify("success", "Template deleted");
      load();
    } catch (err) {
      notify("error", "Could not delete template", errorText(err));
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", sorter: (a, b) => a.title.localeCompare(b.title) },
    {
      title: "Product",
      dataIndex: "product_name",
      // System templates are sent on an event and belong to no product.
      render: (value, record) =>
        record.system_event ? <Tag color="purple">System</Tag> : value,
    },
    { title: "Category", dataIndex: "template_category_name" },
    { title: "Default Subject", dataIndex: "subject", ellipsis: true },
    {
      title: "Built with",
      dataIndex: "design_json",
      render: (value) =>
        value ? <Tag color="geekblue">Designer</Tag> : <Tag>Rich text</Tag>,
    },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          <Tooltip title="Preview">
            <a onClick={() => setPreviewing(record)}>
              <EyeOutlined />
            </a>
          </Tooltip>
          <a onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          {/* A deleted system template would just come back with default
              copy on the next send; switching it off is the way to stop it. */}
          {!record.system_event && (
            <Popconfirm title="Delete this template?" onConfirm={() => remove(record.id)}>
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          The subject is the default and can be tweaked per send.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Add Email Template
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <Modal
        title={previewing?.title || "Preview"}
        open={!!previewing}
        onCancel={() => setPreviewing(null)}
        footer={null}
        width="70%"
        destroyOnClose
      >
        <Paragraph style={{ marginBottom: 8 }}>
          <Text type="secondary">Subject: </Text>
          <Text strong>{previewing?.subject}</Text>
        </Paragraph>
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Placeholders are shown as written; they are filled in per lead when sent.
        </Text>
        {/* An iframe, not innerHTML: a full email document brings its own
            <style> and <body>, which would otherwise restyle this page. */}
        <iframe
          title="Email template preview"
          sandbox=""
          srcDoc={previewing?.html_content || ""}
          style={{ width: "100%", height: "70vh", border: "1px solid #f0f0f0", borderRadius: 4 }}
        />
      </Modal>
      <Modal
        title={editing ? "Edit Email Template" : "Add Email Template"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={mode === "design" ? "95%" : "70%"}
        style={mode === "design" ? { top: 20 } : undefined}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          {editing?.system_event && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="System template"
              description={`Sent automatically: ${editing.system_event_label}. It belongs to no product. Mark it inactive to stop sending it.`}
            />
          )}
          <Row gutter={16}>
            <Col span={editing?.system_event ? 24 : 8}>
              <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            {!editing?.system_event && (
              <>
                <Col span={8}>
                  <Form.Item label="Product" name="product" rules={[{ required: true }]}>
                    <Select
                      options={productOptions(products)}
                      onChange={(value) => {
                        setFormProduct(value);
                        form.setFieldValue("template_category", undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Category"
                    name="template_category"
                    rules={[{ required: true }]}
                  >
                    <Select
                      disabled={!formProduct}
                      placeholder={formProduct ? "Select" : "Pick a product first"}
                      options={categories.map((item) => ({ label: item.name, value: item.id }))}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          <Form.Item
            label="Default Subject"
            name="subject"
            rules={[{ required: true, message: "A default subject is required." }]}
          >
            <Input placeholder="Welcome to {{ products }}, {{ first_name }}" />
          </Form.Item>

          {variables.length > 0 && (
            <Alert
              type="info"
              style={{ marginBottom: 12 }}
              message="Available placeholders"
              description={
                <Paragraph style={{ marginBottom: 0 }}>
                  {variables.map((variable) => (
                    <Tag key={variable.key} style={{ marginBottom: 4 }}>
                      {`{{ ${variable.key} }}`}
                    </Tag>
                  ))}
                </Paragraph>
              }
            />
          )}

          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text strong>Body</Text>
            <Radio.Group
              value={mode}
              size="small"
              onChange={(event) => setMode(event.target.value)}
            >
              <Radio.Button value="rich">Rich text</Radio.Button>
              <Radio.Button value="html">HTML</Radio.Button>
              <Radio.Button value="design">Drag &amp; drop</Radio.Button>
            </Radio.Group>
          </Flex>
          <div style={{ marginBottom: 16 }}>
            {mode === "rich" ? (
              <QEditor key={editing?.id || "new"} value={html} setValue={setHtml} />
            ) : mode === "html" ? (
              <CodeMirrorEditor
                key={editing?.id || "new"}
                initialValue={html}
                setCodeMirrorData={setHtml}
              />
            ) : (
              <div style={{ minHeight: 500 }}>
                <EmailEditor
                  ref={builderRef}
                  minHeight={500}
                  onReady={(editor) => {
                    // Reopen the saved design; a template written as HTML or
                    // rich text opens as one HTML block holding that content.
                    // The ref only exposes `{ editor }`, so load through the
                    // instance handed in here.
                    if (design) editor.loadDesign(design);
                    else if (html.trim()) editor.loadDesign(htmlDesign(html));
                  }}
                />
              </div>
            )}
          </div>

          <Form.Item label="Plain text fallback" name="plain_text_content">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ========== WhatsApp templates ==========

const META_LANGUAGES = [
  ["en", "English"], ["en_US", "English (US)"], ["en_GB", "English (UK)"],
  ["hi", "Hindi"], ["mr", "Marathi"], ["gu", "Gujarati"], ["ta", "Tamil"],
  ["te", "Telugu"], ["kn", "Kannada"], ["ml", "Malayalam"], ["bn", "Bengali"],
  ["pa", "Punjabi"], ["ur", "Urdu"],
];
const META_CATEGORIES = [
  ["MARKETING", "Marketing"], ["UTILITY", "Utility"], ["AUTHENTICATION", "Authentication"],
];
const BUTTON_TYPES = [
  ["QUICK_REPLY", "Quick reply"], ["URL", "Visit website"], ["PHONE_NUMBER", "Call phone"],
];
// A free-form message has no call button: up to three quick replies, or one link.
const MANUAL_BUTTON_TYPES = BUTTON_TYPES.filter(([value]) => value !== "PHONE_NUMBER");

const HEADER_TYPES = [
  ["NONE", "None"], ["TEXT", "Text"], ["IMAGE", "Image"], ["VIDEO", "Video"], ["DOCUMENT", "Document"],
];
const MEDIA_HEADERS = {
  IMAGE: { accept: "image/jpeg,image/png", hint: "JPG or PNG, up to 5 MB" },
  VIDEO: { accept: "video/mp4,video/3gpp", hint: "MP4 or 3GP, up to 16 MB" },
  DOCUMENT: {
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
    hint: "PDF or Office document, up to 100 MB",
  },
};

const STATUS_COLORS = {
  APPROVED: "green", PENDING: "orange", IN_APPEAL: "orange", DRAFT: "default",
  REJECTED: "red", PAUSED: "volcano", DISABLED: "red", DELETED: "default",
};

// Sample values Meta needs for review, guessed from what a slot is mapped to.
const SAMPLE_FOR = {
  first_name: "Asha", last_name: "Rao", full_name: "Asha Rao", company_name: "Acme Studio",
  email: "asha@example.com", phone_number: "919876543210", wa_number: "919876543210",
  lead_number: "L2026090001", job_title: "Founder", assigned_to: "Rohan", today: "23 Sep 2026",
};
const sampleFor = (mapped) => {
  const match = /^{{\s*(\w+)\s*}}$/.exec(String(mapped || "").trim());
  if (match) return SAMPLE_FOR[match[1]] || match[1];
  return String(mapped || "").trim();
};

// Manual templates' {{ first_name }} placeholders, filled for the preview.
const fillNamed = (text) =>
  String(text || "").replace(/{{\s*([\w.<>]+)\s*}}/g, (whole, key) => SAMPLE_FOR[key] || whole);

const findComponent = (components, type) =>
  (components || []).find((c) => String(c.type).toUpperCase() === type) || {};

/** Split Meta components back into the composer's fields. */
const composeFrom = (template) => {
  const components = template?.components || [];
  const header = findComponent(components, "HEADER");
  const body = findComponent(components, "BODY");
  const headerType = header.type ? String(header.format || "TEXT").toUpperCase() : "NONE";
  return {
    meta_name: template?.name || "",
    meta_language: template?.language || "en",
    meta_category: template?.category || "MARKETING",
    meta_number: template?.business_number || undefined,
    header_type: headerType,
    header_text: headerType === "TEXT" ? header.text || "" : "",
    header_media: template?.header_media || null,
    body_text: body.text || "",
    footer_text: findComponent(components, "FOOTER").text || "",
    buttons: (findComponent(components, "BUTTONS").buttons || []).map((b) => ({
      type: b.type, text: b.text, url: b.url, phone_number: b.phone_number,
    })),
    samples: {
      header: Object.fromEntries(
        (header.example?.header_text || []).map((value, i) => [String(i + 1), value])
      ),
      body: Object.fromEntries(
        ((body.example?.body_text || [])[0] || []).map((value, i) => [String(i + 1), value])
      ),
    },
  };
};

/** A manual template's fields, in the same shape the composer edits. */
const manualFrom = (record) => ({
  header_type: record?.manual_header_type || "NONE",
  header_text: record?.manual_header_text || "",
  header_media: record?.manual_header_media || null,
  body_text: record?.manual_body || "",
  footer_text: record?.manual_footer || "",
  buttons: record?.manual_buttons || [],
});

/** Meta's sample file URL, for a synced template with no file stored here. */
const sampleLink = (components) => {
  const handle = findComponent(components, "HEADER").example?.header_handle?.[0];
  return handle && String(handle).startsWith("http") ? handle : null;
};

const metaStatusTag = (record) => {
  const detail = record.message_template_detail;
  if (record.kind === "MANUAL") return <Text type="secondary">—</Text>;
  if (!detail) return <Tag>UNKNOWN</Tag>;
  const tags = [
    <Tag key="s" color={STATUS_COLORS[detail.status] || "orange"}>{detail.status || "UNKNOWN"}</Tag>,
  ];
  if (detail.needs_push) tags.push(<Tag key="p" color="blue">Waiting for sync</Tag>);
  const reason = detail.last_push_error || detail.rejected_reason;
  return reason ? (
    <Tooltip title={reason}>
      <Space size={0}>{tags}<InfoCircleOutlined style={{ color: "#cf1322" }} /></Space>
    </Tooltip>
  ) : (
    <Space size={0}>{tags}</Space>
  );
};

/** Pick and upload a header file; the field's value is the stored file. */
const HeaderMediaInput = ({ value, onChange, headerType, disabled }) => {
  const notify = useNotification();
  const [uploading, setUploading] = useState(false);
  const rules = MEDIA_HEADERS[headerType];
  const { url } = useAuthedMedia(value?.url);

  const upload = async ({ file }) => {
    setUploading(true);
    try {
      const response = await api.uploadWhatsappMedia(file, headerType);
      onChange?.(response.data);
    } catch (err) {
      notify("error", "Upload failed", errorText(err));
    }
    setUploading(false);
  };

  return (
    <Flex align="center" gap="middle" wrap>
      <Upload
        accept={rules?.accept}
        showUploadList={false}
        customRequest={upload}
        disabled={disabled || uploading}
      >
        <Button icon={<UploadOutlined />} loading={uploading} disabled={disabled}>
          {value ? "Replace file" : `Upload ${headerType.toLowerCase()}`}
        </Button>
      </Upload>
      {value ? (
        <Space>
          {headerType === "IMAGE" && url && (
            <img src={url} alt="" style={{ height: 36, borderRadius: 4, objectFit: "cover" }} />
          )}
          <Text>{value.filename}</Text>
          {!disabled && (
            <a aria-label="Remove file" onClick={() => onChange?.(null)}>
              <DeleteOutlined />
            </a>
          )}
        </Space>
      ) : (
        <Text type="secondary">{rules?.hint}</Text>
      )}
    </Flex>
  );
};

/** The live bubble beside the editor. */
const TemplatePreview = ({ layout, media, fallbackLink }) => {
  const { url } = useAuthedMedia(media?.url);
  return (
    <div style={{ position: "sticky", top: 0 }}>
      <Text strong>Preview</Text>
      <div style={{ marginTop: 6 }}>
        <WhatsAppPreview
          layout={layout}
          mediaUrl={url || fallbackLink}
          media={media}
          title="Sample values are filled in"
        />
      </div>
    </div>
  );
};

/**
 * What a saved template looks like when sent: a manual one's placeholders, or
 * a Meta one's {{n}} slots, filled with sample values.
 */
const savedTemplatePreview = (record) => {
  if (record?.kind === "MANUAL") {
    const content = manualFrom(record);
    return {
      layout: {
        header_type: content.header_type,
        header: fillNamed(content.header_text),
        body: fillNamed(content.body_text),
        footer: content.footer_text,
        buttons: content.buttons,
      },
      media: content.header_media,
      link: null,
    };
  }
  const detail = record?.message_template_detail;
  if (!detail) return null;
  const content = composeFrom(detail);
  // The slot's mapping first ({{ first_name }} -> a sample name, or its
  // fixed text), then the example Meta reviewed.
  const fill = (component, text) =>
    String(text || "").replace(/{{\s*(\d+)\s*}}/g, (whole, index) =>
      sampleFor(record.variable_mapping?.[component]?.[index]) ||
      content.samples?.[component]?.[index] ||
      whole
    );
  return {
    layout: {
      header_type: content.header_type,
      header: fill("header", content.header_text),
      body: fill("body", content.body_text),
      footer: content.footer_text,
      buttons: content.buttons,
    },
    media: content.header_media,
    link: sampleLink(detail.components),
  };
};

const WhatsappTemplatePreviewModal = ({ record, onClose }) => {
  const preview = savedTemplatePreview(record);
  const detail = record?.message_template_detail;
  return (
    <Modal
      title={record?.title || "Preview"}
      open={!!record}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      <Space wrap size={[4, 4]} style={{ marginBottom: 12 }}>
        {record?.kind === "MANUAL" ? (
          <Tag color="purple">Manual</Tag>
        ) : (
          <Tag color="blue">Meta</Tag>
        )}
        {record?.product_name && <Tag>{record.product_name}</Tag>}
        {record?.template_category_name && <Tag>{record.template_category_name}</Tag>}
        {detail?.name && (
          <Text type="secondary">
            {detail.name} · {detail.language}
          </Text>
        )}
        {record && record.kind !== "MANUAL" && metaStatusTag(record)}
      </Space>
      {preview ? (
        <TemplatePreview layout={preview.layout} media={preview.media} fallbackLink={preview.link} />
      ) : (
        <Text type="secondary">
          This template is not linked to a Meta template yet, so there is nothing to show.
        </Text>
      )}
    </Modal>
  );
};

const WhatsappTemplateTab = () => {
  const notify = useNotification();
  const products = useProducts();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [formProduct, setFormProduct] = useState(null);
  const [metaTemplateId, setMetaTemplateId] = useState(null);
  const [mapping, setMapping] = useState({});
  const [samples, setSamples] = useState({});
  // META is sent through Meta (starts conversations); MANUAL is free-form,
  // delivered only inside the 24-hour reply window.
  const [kind, setKind] = useState("META");
  // For META: write a new template here, or wrap one already on Meta.
  const [source, setSource] = useState("new");
  const [deleteFromMeta, setDeleteFromMeta] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [form] = Form.useForm();

  // Header, body, footer and buttons are the same fields for both kinds;
  // save() maps them onto the manual_* fields or Meta's components.
  const headerType = Form.useWatch("header_type", form) || "NONE";
  const headerText = Form.useWatch("header_text", form);
  const headerMedia = Form.useWatch("header_media", form);
  const bodyText = Form.useWatch("body_text", form);
  const footerText = Form.useWatch("footer_text", form);
  const buttons = Form.useWatch("buttons", form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadWhatsappTemplates()));
    } catch (err) {
      notify("error", "Could not load templates", errorText(err));
    }
    setLoading(false);
  }, [notify]);

  const loadMeta = useCallback(() => {
    api
      .loadMetaTemplates()
      .then((response) => setMetaTemplates(api.rowsOf(response)))
      .catch(() => setMetaTemplates([]));
  }, []);

  useEffect(() => {
    load();
    loadMeta();
    api
      .loadVariables()
      .then((response) => setVariables(response.data?.variables || []))
      .catch(() => setVariables([]));
  }, [load, loadMeta]);

  useEffect(() => {
    if (!formProduct) {
      setCategories([]);
      setNumbers([]);
      return;
    }
    api
      .loadCategories({ product: formProduct, is_active: true })
      .then((response) => setCategories(api.rowsOf(response)))
      .catch(() => setCategories([]));
    api
      .loadWhatsappConfigurations({ product: formProduct, is_active: true })
      .then((response) => setNumbers(api.rowsOf(response)))
      .catch(() => setNumbers([]));
  }, [formProduct]);

  // A number's templates are its WABA's, so default to the product's default number.
  useEffect(() => {
    if (!open || !numbers.length || form.getFieldValue("meta_number")) return;
    const preferred = numbers.find((n) => n.is_default) || numbers[0];
    form.setFieldValue("meta_number", preferred?.number?.id);
  }, [numbers, open, form]);

  const selectedMeta = metaTemplates.find((item) => item.id === metaTemplateId);
  const editingMeta = editing?.message_template_detail;
  const composing =
    kind === "META" && (editing ? !!editingMeta?.editable : source === "new");
  const manual = kind === "MANUAL";
  // Writing the content here (either kind), rather than wrapping a synced one.
  const authoring = manual || composing;

  const headerSlots = composing
    ? headerType === "TEXT" ? placeholderIndexes(headerText) : []
    : placeholderIndexes(componentText(selectedMeta?.components, "HEADER"));
  const bodySlots = composing
    ? placeholderIndexes(bodyText)
    : placeholderIndexes(componentText(selectedMeta?.components, "BODY"));

  const openForm = (record) => {
    setEditing(record || null);
    setFormProduct(record?.product || null);
    setMetaTemplateId(record?.message_template || null);
    setMapping(record?.variable_mapping || {});
    setKind(record?.kind || "META");
    setSource(record ? "existing" : "new");
    const composed = composeFrom(record?.message_template_detail);
    setSamples(record ? composed.samples : {});
    setOpen(true);
    const content = record?.kind === "MANUAL" ? manualFrom(record) : composed;
    form.setFieldsValue({
      title: record?.title || "",
      product: record?.product,
      template_category: record?.template_category,
      message_template: record?.message_template,
      kind: record?.kind || "META",
      description: record?.description || "",
      is_active: record ? record.is_active : true,
      meta_language: "en",
      meta_category: "MARKETING",
      ...(record ? composed : {}),
      ...(record ? content : { header_type: "NONE", buttons: [] }),
    });
  };

  const setMappingValue = (component, index, value) => {
    setMapping((current) => {
      const next = { ...current, [component]: { ...(current[component] || {}) } };
      if (value === "") {
        delete next[component][index];
        if (!Object.keys(next[component]).length) delete next[component];
      } else {
        next[component][index] = value;
      }
      return next;
    });
  };

  const setSample = (component, index, value) =>
    setSamples((current) => ({
      ...current,
      [component]: { ...(current[component] || {}), [index]: value },
    }));

  const sampleValue = (component, index) =>
    samples?.[component]?.[index] ?? sampleFor(mapping?.[component]?.[index]);

  const fillSlots = (component, text) =>
    String(text || "").replace(/{{\s*(\d+)\s*}}/g, (whole, index) =>
      sampleValue(component, index) || whole
    );

  // Meta's next {{n}}: one past the highest already in the body.
  const nextBodySlot = () => {
    const used = placeholderIndexes(form.getFieldValue("body_text")).map(Number);
    return `{{${used.length ? Math.max(...used) + 1 : 1}}}`;
  };

  const namedVariables = variables.map((variable) => `{{ ${variable.key} }}`);

  const save = async (values) => {
    const isManual = values.kind === "MANUAL";
    const content = {
      header_type: values.header_type || "NONE",
      header_text: values.header_type === "TEXT" ? values.header_text : "",
      header_media: MEDIA_HEADERS[values.header_type] ? values.header_media : null,
      buttons: (values.buttons || []).filter((button) => button?.text),
    };
    const payload = {
      title: values.title,
      product: values.product,
      template_category: values.template_category,
      kind: values.kind,
      description: values.description,
      is_active: values.is_active,
      // The two kinds are mutually exclusive server-side, so clear whichever
      // half does not apply rather than posting a stale value.
      manual_body: isManual ? values.body_text : "",
      manual_header_type: isManual ? content.header_type : "NONE",
      manual_header_text: isManual ? content.header_text : "",
      manual_header_media: isManual ? content.header_media : null,
      manual_footer: isManual ? values.footer_text || "" : "",
      manual_buttons: isManual ? content.buttons : [],
      variable_mapping: isManual ? {} : mapping,
    };
    if (isManual) {
      payload.message_template = null;
    } else if (composing) {
      payload.compose = {
        name: values.meta_name,
        language: values.meta_language,
        category: values.meta_category,
        business_number: values.meta_number,
        header_format: content.header_type,
        header_text: content.header_text,
        header_media: content.header_media,
        body_text: values.body_text,
        footer_text: values.footer_text,
        buttons: content.buttons,
        header_examples: headerSlots.map((i) => sampleValue("header", i)),
        body_examples: bodySlots.map((i) => sampleValue("body", i)),
      };
    } else {
      payload.message_template = values.message_template;
    }
    try {
      if (editing) await api.updateWhatsappTemplate(editing.id, payload);
      else await api.createWhatsappTemplate(payload);
      notify(
        "success",
        `Template ${editing ? "updated" : "created"}`,
        composing ? "Click “Sync with Meta” to submit it for approval." : undefined
      );
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save template", errorText(err));
    }
  };

  const remove = async (record) => {
    try {
      await api.deleteWhatsappTemplate(record.id, { fromMeta: deleteFromMeta });
      notify("success", deleteFromMeta ? "Deleted here and on Meta" : "Template removed");
      load();
    } catch (err) {
      notify("error", "Could not delete template", errorText(err));
    }
    setDeleteFromMeta(false);
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const response = await api.syncWhatsappTemplates();
      const results = response.data?.results || [];
      const errors = results.flatMap((r) => [
        ...(r.error ? [`+${r.number}: ${r.error}`] : []),
        ...(r.push_errors || []).map((e) => `${e.name}: ${e.error}`),
      ]);
      const total = (key) => results.reduce((sum, r) => sum + (r[key] || 0), 0);
      const summary =
        `Submitted ${total("pushed")}, pulled ${total("created") + total("updated")}` +
        `, added ${total("linked")} new` +
        (total("removed") ? `, ${total("removed")} gone from Meta` : "");
      if (errors.length) notify("warning", "Synced with problems", `${summary}. ${errors.join(" | ")}`);
      else notify("success", "Synced with Meta", summary);
      load();
      loadMeta();
    } catch (err) {
      notify("error", "Sync failed", errorText(err));
    }
    setSyncing(false);
  };

  const columns = [
    { title: "Title", dataIndex: "title", sorter: (a, b) => a.title.localeCompare(b.title) },
    { title: "Product", dataIndex: "product_name" },
    { title: "Category", dataIndex: "template_category_name" },
    {
      title: "Type",
      dataIndex: "kind",
      render: (value) =>
        value === "MANUAL" ? <Tag color="purple">Manual</Tag> : <Tag color="blue">Meta</Tag>,
    },
    {
      title: "Message",
      render: (_, record) =>
        record.kind === "MANUAL"
          ? record.manual_body
          : `${record.message_template_detail?.name || ""} (${record.language || ""})`,
      ellipsis: true,
    },
    { title: "Meta Status", render: (_, record) => metaStatusTag(record) },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          <Tooltip title="Preview">
            <a onClick={() => setPreviewing(record)}>
              <EyeOutlined />
            </a>
          </Tooltip>
          <a onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          <Popconfirm
            title="Delete this template?"
            description={
              record.message_template_detail?.template_id ? (
                <Checkbox
                  checked={deleteFromMeta}
                  onChange={(event) => setDeleteFromMeta(event.target.checked)}
                >
                  Also delete it from Meta (every product using it)
                </Checkbox>
              ) : null
            }
            onOpenChange={(visible) => !visible && setDeleteFromMeta(false)}
            onConfirm={() => remove(record)}
          >
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  const slotInputs = (component, slots) =>
    slots.length > 0 && (
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ textTransform: "capitalize" }}>
          {component} variables
        </Text>
        {slots.map((index) => (
          <Row gutter={8} key={`${component}-${index}`} style={{ marginTop: 6 }}>
            <Col span={composing ? 14 : 24}>
              <Input
                addonBefore={`{{${index}}}`}
                placeholder="{{ first_name }} — or fixed text"
                value={mapping?.[component]?.[index] ?? ""}
                onChange={(event) => setMappingValue(component, index, event.target.value)}
              />
            </Col>
            {composing && (
              <Col span={10}>
                <Input
                  addonBefore="Sample"
                  placeholder="Shown to Meta for review"
                  value={sampleValue(component, index)}
                  onChange={(event) => setSample(component, index, event.target.value)}
                />
              </Col>
            )}
          </Row>
        ))}
      </div>
    );

  const variableTags = (
    <Paragraph style={{ marginBottom: 0 }}>
      {variables.map((variable) => (
        <Tag key={variable.key} style={{ marginBottom: 4 }}>
          {`{{ ${variable.key} }}`}
        </Tag>
      ))}
    </Paragraph>
  );

  const submitted = !!editingMeta?.template_id;

  // -- the header / body / footer / buttons editor, shared by both kinds ----

  const buttonTypes = manual ? MANUAL_BUTTON_TYPES : BUTTON_TYPES;
  const hasLinkButton = (buttons || []).some((button) => button?.type === "URL");
  const maxButtons = manual ? (hasLinkButton ? 1 : 3) : 10;

  const contentFields = (
    <>
      <Form.Item
        label="Header"
        name="header_type"
        tooltip={
          manual
            ? "With buttons the message goes as an interactive message; a media header without buttons goes as that media, captioned with the body."
            : "A media header needs a sample file, which Meta reviews and which is sent with every message."
        }
        style={{ marginBottom: 8 }}
      >
        <Radio.Group
          size="small"
          optionType="button"
          options={HEADER_TYPES.map(([value, label]) => ({ value, label }))}
        />
      </Form.Item>
      {headerType === "TEXT" && (
        <Form.Item
          name="header_text"
          rules={[{ required: true, message: "Write the header, or pick None." }, { max: 60 }]}
          extra={manual ? "Up to 60 characters." : "Up to 60 characters, at most one variable written {{1}}."}
        >
          <WhatsAppTextEditor
            singleLine
            maxLength={60}
            placeholder="Header text"
            variables={manual ? namedVariables : undefined}
            onAddVariable={manual ? undefined : () => "{{1}}"}
          />
        </Form.Item>
      )}
      {MEDIA_HEADERS[headerType] && (
        <Form.Item
          name="header_media"
          rules={[{ required: true, message: `Upload the ${headerType.toLowerCase()} for the header.` }]}
          extra={
            !manual && submitted && !headerMedia
              ? "Meta's copy of the sample cannot be reused for an edit - upload the file again."
              : undefined
          }
        >
          <HeaderMediaInput headerType={headerType} />
        </Form.Item>
      )}
      <Form.Item
        label="Body"
        name="body_text"
        rules={[
          { required: true, message: "A message body is required." },
          { max: manual && !(buttons || []).length && !MEDIA_HEADERS[headerType] ? 4096 : 1024 },
        ]}
        extra={
          manual
            ? "Placeholders like {{ first_name }} are filled in per lead."
            : "Use {{1}}, {{2}} … for values filled per lead."
        }
      >
        <WhatsAppTextEditor
          rows={6}
          maxLength={manual && !(buttons || []).length && !MEDIA_HEADERS[headerType] ? 4096 : 1024}
          placeholder={
            manual
              ? "Hi {{ first_name }}, following up on…"
              : "Hi {{1}}, thanks for your interest in PixelShield…"
          }
          variables={manual ? namedVariables : undefined}
          onAddVariable={manual ? undefined : nextBodySlot}
        />
      </Form.Item>
      <Form.Item label="Footer (optional)" name="footer_text" rules={[{ max: 60 }]}>
        <Input maxLength={60} showCount placeholder="e.g. Reply STOP to opt out" />
      </Form.Item>
      <Form.List name="buttons">
        {(fields, { add, remove: removeButton }) => (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Buttons (optional)</Text>
            {manual && (
              <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                Up to three quick replies, or one website link.
              </Text>
            )}
            {fields.map(({ key, name }) => (
              <Row gutter={8} key={key} style={{ marginTop: 6 }} align="middle">
                <Col span={6}>
                  <Form.Item name={[name, "type"]} noStyle initialValue="QUICK_REPLY">
                    <Select options={buttonTypes.map(([value, label]) => ({ value, label }))} />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    name={[name, "text"]}
                    noStyle
                    rules={[{ required: true, max: manual ? 20 : 25 }]}
                  >
                    <Input placeholder="Button text" maxLength={manual ? 20 : 25} />
                  </Form.Item>
                </Col>
                <Col span={9}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, next) =>
                      prev.buttons?.[name]?.type !== next.buttons?.[name]?.type
                    }
                  >
                    {({ getFieldValue }) => {
                      const type = getFieldValue(["buttons", name, "type"]);
                      if (type === "URL")
                        return (
                          <Form.Item name={[name, "url"]} noStyle rules={[{ required: true }]}>
                            <Input placeholder="https://…" />
                          </Form.Item>
                        );
                      if (type === "PHONE_NUMBER")
                        return (
                          <Form.Item name={[name, "phone_number"]} noStyle rules={[{ required: true }]}>
                            <Input placeholder="+919876543210" />
                          </Form.Item>
                        );
                      return null;
                    }}
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <a aria-label="Remove button" onClick={() => removeButton(name)}>
                    <DeleteOutlined />
                  </a>
                </Col>
              </Row>
            ))}
            {fields.length < maxButtons && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                style={{ marginTop: 6, display: "block" }}
                onClick={() => add({ type: "QUICK_REPLY", text: "" })}
              >
                Add button
              </Button>
            )}
          </div>
        )}
      </Form.List>
    </>
  );

  // What the preview shows: what is being written, or the synced template.
  const previewLayout = authoring
    ? {
        header_type: headerType,
        header: manual ? fillNamed(headerText) : fillSlots("header", headerText),
        body: manual ? fillNamed(bodyText) : fillSlots("body", bodyText),
        footer: footerText,
        buttons: (buttons || []).filter((button) => button?.text),
      }
    : selectedMeta && {
        header_type: String(
          findComponent(selectedMeta.components, "HEADER").format ||
            (findComponent(selectedMeta.components, "HEADER").type ? "TEXT" : "NONE")
        ).toUpperCase(),
        header: fillSlots("header", componentText(selectedMeta.components, "HEADER")),
        body: fillSlots("body", componentText(selectedMeta.components, "BODY")),
        footer: componentText(selectedMeta.components, "FOOTER"),
        buttons: findComponent(selectedMeta.components, "BUTTONS").buttons || [],
      };
  const previewMedia = authoring ? headerMedia : selectedMeta?.header_media;
  const previewLink = authoring
    ? !headerMedia && editingMeta && sampleLink(editingMeta.components)
    : selectedMeta && sampleLink(selectedMeta.components);

  return (
    <>
      <Flex justify="space-between" align="center" gap="small" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Meta templates start conversations; manual ones are canned replies inside the
          24-hour window. Sync submits new and edited templates to Meta and brings back
          approvals and templates made in WhatsApp Manager.
        </Text>
        <Space>
          <Button icon={<SyncOutlined spin={syncing} />} loading={syncing} onClick={sync}>
            Sync with Meta
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
            Add WhatsApp Template
          </Button>
        </Space>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <WhatsappTemplatePreviewModal record={previewing} onClose={() => setPreviewing(null)} />
      <Modal
        title={editing ? "Edit WhatsApp Template" : "Add WhatsApp Template"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width="85%"
        style={{ top: 24 }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Row gutter={24}>
            <Col xs={24} lg={15}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Product" name="product" rules={[{ required: true }]}>
                    <Select
                      options={productOptions(products)}
                      onChange={(value) => {
                        setFormProduct(value);
                        form.setFieldValue("template_category", undefined);
                        form.setFieldValue("meta_number", undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Category" name="template_category" rules={[{ required: true }]}>
                    <Select
                      disabled={!formProduct}
                      placeholder={formProduct ? "Select" : "Pick a product first"}
                      options={categories.map((item) => ({ label: item.name, value: item.id }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Space size="large" align="start">
                <Form.Item label="Template Type" name="kind" rules={[{ required: true }]}>
                  <Radio.Group onChange={(event) => setKind(event.target.value)} disabled={!!editing}>
                    <Radio.Button value="META">Meta template</Radio.Button>
                    <Radio.Button value="MANUAL">Manual</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {kind === "META" && !editing && (
                  <Form.Item label="Source">
                    <Radio.Group value={source} onChange={(event) => {
                      setSource(event.target.value);
                      setMapping({});
                    }}>
                      <Radio.Button value="new">Write new</Radio.Button>
                      <Radio.Button value="existing">Use synced</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                )}
              </Space>

              {manual && (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Only deliverable inside the 24-hour reply window"
                    description="WhatsApp accepts free-form messages only within 24 hours of the contact's last message to you. To start a conversation, use a Meta template."
                  />
                  {contentFields}
                </>
              )}

              {kind === "META" && composing && (
                <>
                  {editing && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        submitted
                          ? "Edits go to Meta on the next Sync and are reviewed again. The name and language cannot change once submitted."
                          : "Not submitted yet — Sync sends it to Meta for approval."
                      }
                    />
                  )}
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Template name"
                        name="meta_name"
                        rules={[
                          { required: true },
                          { pattern: /^[a-z0-9_]+$/, message: "Lowercase letters, numbers and _ only" },
                        ]}
                      >
                        <Input placeholder="pixelshield_demo_invite" disabled={submitted} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item label="Language" name="meta_language" rules={[{ required: true }]}>
                        <Select
                          showSearch
                          disabled={submitted}
                          options={META_LANGUAGES.map(([value, label]) => ({ value, label: `${label} (${value})` }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item label="Meta category" name="meta_category" rules={[{ required: true }]}>
                        <Select
                          disabled={submitted && editingMeta?.status === "APPROVED"}
                          options={META_CATEGORIES.map(([value, label]) => ({ value, label }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="WhatsApp number"
                        name="meta_number"
                        rules={[{ required: true, message: "Add a number in WhatsApp Configuration" }]}
                        tooltip="The template is created on this number's WhatsApp Business Account."
                      >
                        <Select
                          disabled={submitted || !formProduct}
                          placeholder={formProduct ? "Select" : "Pick a product first"}
                          notFoundContent="No WhatsApp number for this product"
                          options={numbers.map((config) => ({
                            value: config.number?.id,
                            label: `${config.name} (+${config.number?.phone_number})`,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {contentFields}
                </>
              )}

              {kind === "META" && !composing && editing && editingMeta && !editingMeta.editable && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="This template has a location header or dynamic buttons, so its content is edited in WhatsApp Manager. Sync brings the changes here."
                />
              )}

              {kind === "META" && !composing && (
                <Form.Item label="Meta Template" name="message_template" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    disabled={!!editing && !!editingMeta}
                    notFoundContent="Nothing synced yet — click “Sync with Meta”"
                    onChange={(value) => {
                      setMetaTemplateId(value);
                      setMapping({});
                    }}
                    options={metaTemplates
                      .filter((item) => item.status !== "DELETED")
                      .map((item) => ({
                        label: `${item.name} · ${item.language} · ${item.status}`,
                        value: item.id,
                      }))}
                  />
                </Form.Item>
              )}

              {kind === "META" && (headerSlots.length > 0 || bodySlots.length > 0) && (
                <>
                  <Alert
                    type="info"
                    style={{ marginBottom: 12 }}
                    message="Fill each slot with a placeholder or fixed text"
                    description={variableTags}
                  />
                  {slotInputs("header", headerSlots)}
                  {slotInputs("body", bodySlots)}
                </>
              )}

              <Form.Item label="Description" name="description">
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item name="is_active" valuePropName="checked">
                <Checkbox>Active</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} lg={9}>
              <TemplatePreview
                layout={previewLayout}
                media={previewMedia}
                fallbackLink={previewLink}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ========== Test sends ==========

/**
 * Sends one test message through a configuration. The backend sends inline,
 * so the notification shows the SMTP server's / Meta's own answer.
 */
const TestSendModal = ({ channel, configuration, onClose }) => {
  const notify = useNotification();
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const isEmail = channel === "email";

  const send = async (values) => {
    setSending(true);
    try {
      const response = isEmail
        ? await api.sendTestEmail(configuration.id, values)
        : await api.sendTestWhatsapp(configuration.id, {
            to_number: values.to_number,
            ...(values.mode === "text"
              ? { text: values.text }
              : { template_name: values.template_name, language: values.language }),
          });
      notify("success", "Test sent", response.data?.message);
      onClose();
    } catch (err) {
      notify("error", "Test failed", errorText(err));
    }
    setSending(false);
  };

  return (
    <Modal
      title={`Send test ${isEmail ? "email" : "WhatsApp"} · ${configuration?.name || ""}`}
      open={!!configuration}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Send"
      confirmLoading={sending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={send}
        preserve={false}
        initialValues={{ mode: "template", template_name: "hello_world", language: "en_US" }}
      >
        {isEmail ? (
          <Form.Item
            label="Send to"
            name="to_email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input placeholder="you@example.com" />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              label="Send to"
              name="to_number"
              rules={[{ required: true }]}
              help="With country code, e.g. 919876543210"
            >
              <Input placeholder="919876543210" />
            </Form.Item>
            <Form.Item name="mode" label="Message">
              <Radio.Group>
                <Radio.Button value="template">Template</Radio.Button>
                <Radio.Button value="text">Free text</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.mode !== next.mode}>
              {({ getFieldValue }) =>
                getFieldValue("mode") === "text" ? (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Free text only reaches a number that messaged this business number in the last 24 hours."
                    />
                    <Form.Item name="text" rules={[{ required: true }]}>
                      <TextArea rows={3} placeholder="Test message from TesonCRM" />
                    </Form.Item>
                  </>
                ) : (
                  <Row gutter={16}>
                    <Col span={16}>
                      <Form.Item
                        label="Approved template"
                        name="template_name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Language" name="language" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              }
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

// ========== Email configurations ==========

const EmailConfigurationTab = () => {
  const notify = useNotification();
  const products = useProducts();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(null);
  const [viewingLogs, setViewingLogs] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadEmailConfigurations()));
    } catch (err) {
      notify("error", "Could not load configurations", errorText(err));
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (record) => {
    setEditing(record || null);
    setOpen(true);
    form.setFieldsValue(
      record || {
        name: "",
        product: undefined,
        from_email: "",
        from_name: "",
        reply_to: "",
        host: "",
        port: undefined,
        username: "",
        password: "",
        use_tls: true,
        use_ssl: false,
        daily_limit: null,
        send_delay_seconds: 0,
        is_default: false,
        is_active: true,
      }
    );
  };

  const save = async (values) => {
    // Never PATCH an empty password over a stored one.
    const payload = { ...values };
    if (editing && !payload.password) delete payload.password;
    try {
      if (editing) await api.updateEmailConfiguration(editing.id, payload);
      else await api.createEmailConfiguration(payload);
      notify("success", `Configuration ${editing ? "updated" : "created"}`);
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save configuration", errorText(err));
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.setDefaultEmailConfiguration(id);
      notify("success", "Default updated");
      load();
    } catch (err) {
      notify("error", "Could not set default", errorText(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteEmailConfiguration(id);
      notify("success", "Configuration deleted");
      load();
    } catch (err) {
      notify("error", "Could not delete configuration", errorText(err));
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Product", dataIndex: "product_name" },
    { title: "From", dataIndex: "sender" },
    { title: "SMTP Host", dataIndex: "host", render: (value) => value || <Text type="secondary">Project default</Text> },
    {
      title: "Sent Today",
      dataIndex: "usage_today",
      render: (usage, record) => {
        const sent = usage?.sent ?? 0;
        const queued = usage?.queued ?? 0;
        const full = record.daily_limit && sent + queued >= record.daily_limit;
        return (
          <Space size={4}>
            <Text type={full ? "danger" : undefined}>
              {sent}
              {record.daily_limit ? ` / ${record.daily_limit}` : ""}
            </Text>
            {queued > 0 && (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {queued} queued
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Gap",
      dataIndex: "send_delay_seconds",
      render: (value) => (value ? `${value}s` : <Text type="secondary">None</Text>),
    },
    {
      title: "Default",
      dataIndex: "is_default",
      render: (value) => (value ? <Tag color="gold" icon={<CheckCircleOutlined />}>Default</Tag> : null),
    },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          {!record.is_default && (
            <a title="Make default" onClick={() => makeDefault(record.id)}>
              <StarOutlined />
            </a>
          )}
          <a title="Send test" onClick={() => setTesting(record)}>
            <SendOutlined />
          </a>
          <a title="Logs" onClick={() => setViewingLogs(record)}>
            <HistoryOutlined />
          </a>
          <a onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          <Popconfirm title="Delete this configuration?" onConfirm={() => remove(record.id)}>
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          One default per product. Leave the SMTP fields blank to send through the project's
          mail settings and only override the addresses.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Add Email Configuration
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <TestSendModal channel="email" configuration={testing} onClose={() => setTesting(null)} />
      <MessageLogsDrawer
        key={viewingLogs?.id}
        channel="email"
        configuration={viewingLogs}
        onClose={() => setViewingLogs(null)}
      />
      <Modal
        title={editing ? "Edit Email Configuration" : "Add Email Configuration"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="Sales mailbox" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Product" name="product" rules={[{ required: true }]}>
                <Select options={productOptions(products)} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="From Email"
                name="from_email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input placeholder="sales@teson.in" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="From Name" name="from_name">
                <Input placeholder="Teson Sales" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Reply To" name="reply_to" rules={[{ type: "email" }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item label="SMTP Host" name="host">
                <Input placeholder="Blank = project mail settings" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Port" name="port">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Username" name="username">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Password"
            name="password"
            help={editing ? "Leave blank to keep the stored password." : null}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Daily Limit"
                name="daily_limit"
                help="Most emails per day. Blank = no limit; extra emails wait for the next day."
              >
                <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="No limit" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Delay Between Emails (seconds)"
                name="send_delay_seconds"
                help="Minimum gap between two emails from this mailbox."
              >
                <InputNumber min={0} max={86400} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Space size="large">
            <Form.Item name="use_tls" valuePropName="checked" noStyle>
              <Checkbox>Use TLS</Checkbox>
            </Form.Item>
            <Form.Item name="use_ssl" valuePropName="checked" noStyle>
              <Checkbox>Use SSL</Checkbox>
            </Form.Item>
            <Form.Item name="is_default" valuePropName="checked" noStyle>
              <Checkbox>Default for this product</Checkbox>
            </Form.Item>
            <Form.Item name="is_active" valuePropName="checked" noStyle>
              <Checkbox>Active</Checkbox>
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
};

// ========== WhatsApp configurations ==========

// Where Meta should POST messages and template status updates for a number.
const webhookUrl = () =>
  `${String(process.env.REACT_APP_BASE_URL || "/api/").replace(/\/+$/, "")}/whatsapp_api/wa_webhook/`;

const WhatsappConfigurationTab = () => {
  const notify = useNotification();
  const products = useProducts();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(null);
  const [checking, setChecking] = useState(null);
  const [viewingLogs, setViewingLogs] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadWhatsappConfigurations()));
    } catch (err) {
      notify("error", "Could not load configurations", errorText(err));
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (record) => {
    setEditing(record || null);
    setOpen(true);
    const number = record?.number || {};
    form.setFieldsValue({
      name: record?.name || "",
      product: record?.product,
      phone_number: number.phone_number || "",
      phone_number_id: number.phone_number_id || "",
      business_account_id: number.business_account_id || "",
      access_token: "",
      webhook_verify_token: number.webhook_verify_token || "",
      is_default: record ? record.is_default : false,
      is_active: record ? record.is_active : true,
    });
  };

  const save = async (values) => {
    // Never PATCH an empty token over the stored one.
    const payload = { ...values };
    if (editing && !payload.access_token) delete payload.access_token;
    try {
      if (editing) await api.updateWhatsappConfiguration(editing.id, payload);
      else await api.createWhatsappConfiguration(payload);
      notify("success", `WhatsApp number ${editing ? "updated" : "added"}`);
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save the WhatsApp number", errorText(err));
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.setDefaultWhatsappConfiguration(id);
      notify("success", "Default updated");
      load();
    } catch (err) {
      notify("error", "Could not set default", errorText(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteWhatsappConfiguration(id);
      notify("success", "Configuration deleted");
      load();
    } catch (err) {
      notify("error", "Could not delete configuration", errorText(err));
    }
  };

  const checkConnection = async (record) => {
    setChecking(record.id);
    try {
      const response = await api.testWhatsappConnection(record.id);
      notify("success", "Credentials work", response.data?.message);
    } catch (err) {
      notify("error", "Connection failed", errorText(err));
    }
    setChecking(null);
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("success", `${what} copied`);
    } catch (e) {
      notify("error", "Could not copy");
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Product", dataIndex: "product_name" },
    {
      title: "Phone",
      render: (_, record) => (record.number?.phone_number ? `+${record.number.phone_number}` : "—"),
    },
    {
      title: "WhatsApp Business Account",
      render: (_, record) => <Text code>{record.number?.business_account_id || "—"}</Text>,
    },
    {
      title: "Default",
      dataIndex: "is_default",
      render: (value) => (value ? <Tag color="gold" icon={<CheckCircleOutlined />}>Default</Tag> : null),
    },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          {!record.is_default && (
            <a title="Make default" onClick={() => makeDefault(record.id)}>
              <StarOutlined />
            </a>
          )}
          <a title="Check connection" onClick={() => checkConnection(record)}>
            {checking === record.id ? <LoadingOutlined /> : <ApiOutlined />}
          </a>
          <a title="Send test" onClick={() => setTesting(record)}>
            <SendOutlined />
          </a>
          <a title="Logs" onClick={() => setViewingLogs(record)}>
            <HistoryOutlined />
          </a>
          <a title="Edit" onClick={() => openForm(record)}>
            <EditOutlined />
          </a>
          <Popconfirm
            title="Delete this configuration?"
            description="The number and its conversations stay in the inbox."
            onConfirm={() => remove(record.id)}
          >
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          A product can have several WhatsApp numbers; one is its default. Templates belong to
          the number's WhatsApp Business Account and are synced on the WhatsApp Templates tab.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Add WhatsApp Number
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />
      <TestSendModal channel="whatsapp" configuration={testing} onClose={() => setTesting(null)} />
      <MessageLogsDrawer
        key={viewingLogs?.id}
        channel="whatsapp"
        configuration={viewingLogs}
        onClose={() => setViewingLogs(null)}
      />
      <Modal
        title={editing ? "Edit WhatsApp Number" : "Add WhatsApp Number"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        width={760}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="PixelShield Sales" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Product" name="product" rules={[{ required: true }]}>
                <Select options={productOptions(products)} />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="From Meta: WhatsApp Manager → API Setup (or Business Settings → System users for a permanent token)."
          />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Phone Number"
                name="phone_number"
                rules={[{ required: true }]}
                tooltip="With country code, e.g. 919702896240"
              >
                <Input placeholder="919702896240" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phone Number ID" name="phone_number_id" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="WhatsApp Business Account ID"
                name="business_account_id"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Access Token"
            name="access_token"
            rules={editing ? [] : [{ required: true }]}
            help={
              editing
                ? editing.number?.has_access_token
                  ? "A token is stored. Leave blank to keep it."
                  : "No token stored yet."
                : null
            }
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="Webhook Verify Token"
            name="webhook_verify_token"
            help={editing ? null : "Leave blank to generate one."}
          >
            <Input />
          </Form.Item>
          <Card size="small" style={{ marginBottom: 12, background: "#fafafa" }}>
            <Text strong>Webhook for Meta</Text>
            <Paragraph type="secondary" style={{ marginBottom: 6 }}>
              In the Meta app → WhatsApp → Configuration, set this Callback URL and the verify
              token above, and subscribe to <Text code>messages</Text> and{" "}
              <Text code>message_template_status_update</Text>. Replies then reach the inbox and
              template approvals update here automatically.
            </Paragraph>
            <Space>
              <Text code>{webhookUrl()}</Text>
              <a onClick={() => copy(webhookUrl(), "Callback URL")}>
                <CopyOutlined />
              </a>
            </Space>
          </Card>
          <Space size="large">
            <Form.Item name="is_default" valuePropName="checked" noStyle>
              <Checkbox>Default for this product</Checkbox>
            </Form.Item>
            <Form.Item name="is_active" valuePropName="checked" noStyle>
              <Checkbox>Active</Checkbox>
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
};

// ========== Page ==========

const ActivityTemplates = ({ defaultTab = "categories" }) => {
  const items = [
    { key: "categories", label: "Categories", children: <CategoryTab /> },
    { key: "email", label: "Email Templates", children: <EmailTemplateTab /> },
    { key: "whatsapp", label: "WhatsApp Templates", children: <WhatsappTemplateTab /> },
    { key: "email-config", label: "Email Configuration", children: <EmailConfigurationTab /> },
    {
      key: "whatsapp-config",
      label: "WhatsApp Configuration",
      children: <WhatsappConfigurationTab />,
    },
    {
      key: "chatbot-config",
      label: "Chatbot Configuration",
      children: <ChatbotConfigurationTab />,
    },
    { key: "batches", label: "Send Batches", children: <MessageBatchesTab /> },
  ];

  return (
    <div style={{ padding: 10 }}>
      <Card bordered={false}>
        {/* `key` remounts the Tabs when the route changes, so navigating from
            Email Templates to WhatsApp Templates actually switches tab. */}
        <Tabs key={defaultTab} defaultActiveKey={defaultTab} items={items} destroyInactiveTabPane />
      </Card>
    </div>
  );
};

export default ActivityTemplates;
